import { useQuery } from "@connectrpc/connect-query";
import { skipToken } from "@tanstack/react-query";
import { CircleAlertIcon } from "lucide-react";

import { CopyButton } from "@/components/copy-button";
import { ErrorState } from "@/components/error-state";
import { DetailRow } from "@/components/page-header";
import { MessageStateBadge } from "@/components/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { AdminGetMessageResponse } from "@/gen/wabms/admin/v1/admin_pb";
import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import {
  failureReasonLabel,
  formatBoth,
  messageStateIsTerminal,
  messageStateLabel,
  messageStateTone,
  toneSolidClass,
  toneTextClass,
} from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * AdminGetMessage is the only call that returns the rendered content and the
 * full state history, so the sheet fetches by id rather than being handed the
 * row from the table. That also keeps the row list light.
 */
export function MessageDetailSheet({
  messageId,
  onOpenChange,
}: {
  messageId: string | undefined;
  onOpenChange: (open: boolean) => void;
}) {
  const messageQuery = useQuery(
    AdminService.method.getMessage,
    messageId ? { messageId } : skipToken,
  );

  return (
    <Sheet open={messageId !== undefined} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl">
        {messageQuery.isError ? (
          <div className="p-4">
            <ErrorState
              error={messageQuery.error}
              title="Could not load this message"
              onRetry={() => void messageQuery.refetch()}
            />
          </div>
        ) : messageQuery.data?.message ? (
          <MessageDetail detail={messageQuery.data} />
        ) : messageId !== undefined ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-64 rounded-xl" />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function MessageDetail({ detail }: { detail: AdminGetMessageResponse }) {
  // Guarded by the caller; narrow for the rest of the component.
  const message = detail.message;
  if (!message) return null;

  const history = detail.history;
  const metadata = Object.entries(message.metadata);
  const terminal = messageStateIsTerminal(message.state);
  // The response repeats target_message_id at the top level; prefer whichever
  // is populated so an older row still renders.
  const targetMessageId = detail.targetMessageId || message.targetMessageId;

  return (
    <>
      <SheetHeader className="gap-1">
        <SheetTitle className="flex items-center gap-2">
          <MessageStateBadge state={message.state} />
          <span className="truncate font-mono text-sm">{message.messageId}</span>
        </SheetTitle>
        <SheetDescription>
          {terminal ? "Terminal state." : "Still in flight; the state can still change."} Ordering
          key is state_sequence, not the timestamps.
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-5 px-4 pb-6">
          <section>
            <SectionTitle>Identifiers</SectionTitle>
            <dl>
              <DetailRow label="Destination">
                <span className="flex items-center gap-2">
                  <code className="font-mono">{message.destination?.id ?? "—"}</code>
                  {message.destination?.idKind ? (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {message.destination.idKind}
                    </span>
                  ) : null}
                </span>
              </DetailRow>
              <DetailRow label="client_message_id">
                <code className="font-mono text-xs">{message.clientMessageId}</code>
              </DetailRow>
              <DetailRow label="batch_id">
                <code className="font-mono text-xs">{message.batchId}</code>
              </DetailRow>
              <DetailRow label="target_message_id">
                {targetMessageId ? (
                  <span className="flex items-center gap-2">
                    <code className="font-mono text-xs break-all">{targetMessageId}</code>
                    <CopyButton value={targetMessageId} label="" size="xs" variant="ghost" />
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    none — WhatsApp has not acknowledged this message
                  </span>
                )}
              </DetailRow>
              <DetailRow label="state_sequence">
                <span className="tabular">{message.stateSequence.toString()}</span>
              </DetailRow>
              <DetailRow label="attempt_count">
                <span className="tabular">{message.attemptCount}</span>
              </DetailRow>
            </dl>
          </section>

          <Separator />

          <section>
            <SectionTitle>Content</SectionTitle>
            <p className="rounded-lg border bg-muted/40 p-3 text-sm whitespace-pre-wrap">
              {detail.contentText || (
                <span className="text-muted-foreground">
                  The service did not retain the rendered content for this message.
                </span>
              )}
            </p>
          </section>

          {message.error ? (
            <>
              <Separator />
              <section>
                <SectionTitle>Error</SectionTitle>
                <div className="space-y-2 rounded-lg border border-tone-danger/40 bg-tone-danger/5 p-3">
                  <div className="flex items-start gap-2">
                    <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-tone-danger" />
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-medium text-tone-danger">
                        {failureReasonLabel(message.error.reason)}
                      </p>
                      <p className="text-sm">{message.error.description}</p>
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-tone-danger/20 pt-2 text-xs">
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground">Retryable</dt>
                      <dd
                        className={cn(
                          "font-medium",
                          message.error.retryable ? "text-tone-warning" : "text-muted-foreground",
                        )}
                      >
                        {message.error.retryable
                          ? "yes — resubmitting may succeed"
                          : "no — resubmitting will fail the same way"}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground">target_code</dt>
                      <dd className="font-mono">{message.error.targetCode ?? "—"}</dd>
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <dt className="text-muted-foreground">Occurred</dt>
                      <dd>{formatBoth(message.error.occurredAt)}</dd>
                    </div>
                  </dl>
                </div>
              </section>
            </>
          ) : null}

          <Separator />

          <section>
            <SectionTitle>State history</SectionTitle>
            <ol className="relative space-y-4 pl-5">
              <span className="absolute top-1 bottom-1 left-[3px] w-px bg-border" />
              {history.map((event) => {
                const tone = messageStateTone(event.state);
                return (
                  <li key={event.stateSequence.toString()} className="relative">
                    <span
                      className={cn(
                        "absolute top-1.5 -left-5 size-[7px] rounded-full ring-3 ring-background",
                        toneSolidClass(tone),
                      )}
                    />
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span className={cn("text-sm font-medium", toneTextClass(tone))}>
                        {messageStateLabel(event.state)}
                      </span>
                      <span className="tabular text-xs text-muted-foreground">
                        seq {event.stateSequence.toString()} · attempt {event.attemptCount}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{formatBoth(event.occurredAt)}</p>
                    {event.error ? (
                      <p className="mt-1 text-xs text-tone-danger">
                        {failureReasonLabel(event.error.reason)}: {event.error.description}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          </section>

          <Separator />

          <section>
            <SectionTitle>Metadata</SectionTitle>
            {metadata.length === 0 ? (
              <p className="text-sm text-muted-foreground">None.</p>
            ) : (
              <dl className="rounded-lg border">
                {metadata.map(([key, value], i) => (
                  <div
                    key={key}
                    className={cn(
                      "grid grid-cols-[minmax(8rem,auto)_1fr] gap-3 px-3 py-1.5",
                      i > 0 && "border-t",
                    )}
                  >
                    <dt className="font-mono text-xs text-muted-foreground">{key}</dt>
                    <dd className="font-mono text-xs break-all">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Stored verbatim from the submission and echoed on every webhook event.
            </p>
          </section>

          <Separator />

          <section>
            <SectionTitle>Timeline</SectionTitle>
            <dl>
              <DetailRow label="Created">{formatBoth(message.createdAt)}</DetailRow>
              <DetailRow label="State changed">{formatBoth(message.stateChangedAt)}</DetailRow>
            </dl>
          </section>
        </div>
      </ScrollArea>
    </>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
      {children}
    </h3>
  );
}
